import React from 'react';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Box, Grid, Typography, Menu, MenuItem } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import THEME from '../../../constants/theme';
import { ELEMENT_SPACING } from '../../../constants';

/**
 * ScrollableJobs component for displaying filtered and scrollable job listings
 *
 * @param {Object} props - Component props
 * @param {Array} props.jobs - Array of job objects with title, location, description, id
 * @param {Function} props.setJobCount - Function to update job count in parent component
 * @param {string} props.jobType - Type of jobs (e.g., "Jobs", "Internships")
 * @returns {React.Component} ScrollableJobs component
 */
const ScrollableJobs = ({ jobs = [], setJobCount, jobType = 'Jobs' }) => {
  const navigate = useNavigate();
  const [visibleJobs, setVisibleJobs] = useState(new Set([0, 1, 2, 3]));
  const containerRef = useRef(null);
  const jobRefs = useRef([]);

  // State for filtering
  const [selectedTitle, setSelectedTitle] = useState(`All ${jobType}`);
  const [selectedLocation, setSelectedLocation] = useState('All Locations');
  const [anchorElTitle, setAnchorElTitle] = useState(null);
  const [anchorElLocation, setAnchorElLocation] = useState(null);

  // Define order mapping: UK first, then Dubai, Singapore, and LISBON LAST.
  const getLocationOrder = location => {
    // Normalize location to uppercase for comparisons.
    const loc = location ? location.toUpperCase() : '';
    const ukCities = ['LONDON'];
    if (ukCities.includes(loc)) {
      return 0;
    }
    if (loc.includes('DUBAI')) {
      return 1;
    }
    if (loc.includes('SINGAPORE')) {
      return 2;
    }
    if (loc.includes('LISBON')) {
      return 3;
    }
    // Default order if not matched.
    return 4;
  };

  // Extract unique titles and locations
  const uniqueTitles = useMemo(() => {
    const titles = [
      `All ${jobType}`,
      ...Array.from(new Set(jobs.map(job => job.title)))
    ];
    // Sort titles alphabetically (except for "All Jobs"/"All Internships" which stays first)
    return [titles[0], ...titles.slice(1).sort((a, b) => a.localeCompare(b))];
  }, [jobs, jobType]);

  const uniqueLocations = useMemo(() => {
    const locations = [
      'All Locations',
      ...Array.from(new Set(jobs.map(job => job.location)))
    ];

    // Sort locations by our custom order (UK first, Dubai, Singapore, Lisbon last)
    return [
      locations[0], // Keep "All Locations" as the first item
      ...locations.slice(1).sort((a, b) => {
        return getLocationOrder(a) - getLocationOrder(b);
      })
    ];
  }, [jobs]);

  // Filter jobs based on selection
  const filteredJobs = useMemo(() => {
    if (!jobs || jobs.length === 0) return [];

    const filtered = jobs.filter(job => {
      const titleMatches =
        selectedTitle === `All ${jobType}` || job.title === selectedTitle;
      const locationMatches =
        selectedLocation === 'All Locations' ||
        job.location === selectedLocation;
      return titleMatches && locationMatches;
    });

    return [...filtered].sort((a, b) => {
      return getLocationOrder(a.location) - getLocationOrder(b.location);
    });
  }, [jobs, selectedTitle, selectedLocation, jobType]);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const visibleSet = new Set();

    jobRefs.current.forEach((ref, index) => {
      if (
        ref &&
        ref.getBoundingClientRect().top >=
          container.getBoundingClientRect().top &&
        ref.getBoundingClientRect().bottom <=
          container.getBoundingClientRect().bottom
      ) {
        visibleSet.add(index);
      }
    });

    setVisibleJobs(visibleSet);
    setJobCount(visibleSet.size);
  }, [setJobCount]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call to set the visible jobs
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [filteredJobs, handleScroll]);

  const handleJobClick = jobId => {
    // Navigate to the correct job details page using React Router
    navigate(`/how-to-join/${jobId}`);
  };

  // Handle filter dropdown events
  const handleTitleClick = event => {
    setAnchorElTitle(event.currentTarget);
  };

  const handleLocationClick = event => {
    setAnchorElLocation(event.currentTarget);
  };

  const handleCloseTitle = () => {
    setAnchorElTitle(null);
  };

  const handleCloseLocation = () => {
    setAnchorElLocation(null);
  };

  if (!jobs || jobs.length === 0) {
    return (
      <Typography
        variant='body1'
        sx={{
          fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
          fontSize: '1.2rem',
          padding: '1rem 0'
        }}
      >
        No positions currently available. Please check back later.
      </Typography>
    );
  }

  return (
    <React.Fragment>
      {/* Interactive Filter Text */}
      <Box
        sx={{
          backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
          padding: '0 0px',
          mb: 2
        }}
      >
        <Typography
          variant='h5'
          sx={{
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
          }}
        >
          I&apos;m interested in{' '}
          <span
            style={{
              color: THEME.COLORS.PRIMARY_BLUE,
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
            onClick={handleTitleClick}
          >
            {selectedTitle}
          </span>{' '}
          available in the different{' '}
          <span
            style={{
              color: THEME.COLORS.PRIMARY_BLUE,
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
            onClick={handleLocationClick}
          >
            {selectedLocation}
          </span>
          .
        </Typography>
      </Box>

      {/* Dropdown for Titles (alphabetically sorted) */}
      <Menu
        anchorEl={anchorElTitle}
        open={Boolean(anchorElTitle)}
        onClose={handleCloseTitle}
      >
        {uniqueTitles.map((title, index) => (
          <MenuItem
            key={index}
            onClick={() => {
              setSelectedTitle(title);
              handleCloseTitle();
            }}
          >
            {title}
          </MenuItem>
        ))}
      </Menu>

      {/* Dropdown for Locations (sorted by custom order) */}
      <Menu
        anchorEl={anchorElLocation}
        open={Boolean(anchorElLocation)}
        onClose={handleCloseLocation}
      >
        {uniqueLocations.map((location, index) => (
          <MenuItem
            key={index}
            onClick={() => {
              setSelectedLocation(location);
              handleCloseLocation();
            }}
          >
            {location}
          </MenuItem>
        ))}
      </Menu>

      {/* Job Count Display */}
      <Box mt={2}>
        <Typography
          variant='h6'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
            fontSize: '1.2rem'
          }}
        >
          {`Viewing ${Math.min(visibleJobs.size, filteredJobs.length)} of ${filteredJobs.length} ${jobType}`}
        </Typography>
      </Box>

      {/* Job List - Removed border */}
      <Box
        ref={containerRef}
        sx={{
          height: '50vh',
          overflowY: 'auto',
          padding: `${ELEMENT_SPACING} ${ELEMENT_SPACING} 0 ${ELEMENT_SPACING}`,
          mt: 2, // Removed border
          borderRadius: '8px'
        }}
      >
        <Grid container spacing={1.5}>
          {filteredJobs.map((job, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <motion.div
                data-index={index}
                ref={el => (jobRefs.current[index] = el)}
                initial={{ y: 40 }}
                animate={
                  visibleJobs.has(index)
                    ? { y: 0, scale: 1 }
                    : { y: 20, scale: 0.95 }
                }
                transition={{
                  y: { duration: 0.6 },
                  scale: { duration: 0.4 },
                  delay: index * 0.1
                }}
                style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
                  borderRadius: '8px',
                  boxShadow: visibleJobs.has(index)
                    ? '0px 4px 10px rgba(0, 0, 0, 0.1)'
                    : 'none',
                  cursor: 'pointer',
                  border: `1px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`
                }}
                onClick={() => handleJobClick(job.id)}
              >
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant='h6'
                    sx={{
                      fontSize: '1.2rem',
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                      mb: 1
                    }}
                  >
                    <span style={{ color: THEME.COLORS.PRIMARY_BLUE }}>
                      {job.location}
                    </span>{' '}
                    {job.title}
                  </Typography>

                  <Typography
                    variant='body1'
                    sx={{
                      fontSize: '1rem',
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
                    }}
                  >
                    <span
                      style={{
                        color: THEME.COLORS.NEUTRAL_BLACK,
                        textDecoration: 'underline',
                        fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                        cursor: 'pointer'
                      }}
                      onClick={e => {
                        e.stopPropagation(); // Prevent triggering parent click
                        handleJobClick(job.id);
                      }}
                    >
                      {job.title}
                    </span>
                    {' | '}
                    {job.description.length > 150
                      ? job.description.slice(0, 150) + '...'
                      : job.description}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* More Jobs Count */}
      <Typography
        variant='h6'
        mt={2}
        mb={4}
        sx={{
          fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
          fontSize: '1.2rem',
          color: THEME.COLORS.NEUTRAL_GRAY_DARK
        }}
      >
        {`+${filteredJobs.length - visibleJobs.size > 0 ? filteredJobs.length - visibleJobs.size : 0} more ${jobType.toLowerCase()}`}
      </Typography>
    </React.Fragment>
  );
};

export default ScrollableJobs;
